import { Badge } from "@mantine/core";
import { L2KBadgeProps } from "./types";
import { BADGE_VARIANTS } from "./constants";

/**
 * L2KBadge - Branded badge component with themed variants
 *
 * Brand variants: default, accent, subtle, outline, accentDark
 * Skill levels: beginner (green), improver (blue), intermediate (orange), advanced (red)
 * Utility: muted (gray), dot (with dot indicator)
 *
 * Perfect for difficulty levels, categories, tags, and status indicators
 */
export function L2KBadge({
  children,
  variant = "default",
  color,
  style,
  ...props
}: L2KBadgeProps) {
  // Get badge styles based on variant
  const badgeVariant = BADGE_VARIANTS[variant];

  // If custom color is provided, use Mantine's color system instead of custom styles
  if (color) {
    return (
      <Badge
        color={color}
        variant="light"
        style={{
          fontFamily: "'Public Sans', sans-serif",
          fontWeight: 500,
          textTransform: "capitalize" as const,
          ...style,
        }}
        {...props}
      >
        {children}
      </Badge>
    );
  }

  const badgeStyles = {
    background: badgeVariant.background,
    color: badgeVariant.color,
    border: badgeVariant.border,
    fontFamily: "'Public Sans', sans-serif",
    fontWeight: 500,
    textTransform: "capitalize" as const,
    ...style,
  };

  // For dot variant, use Mantine's dot variant
  if (variant === "dot") {
    return (
      <Badge variant="dot" color="orange" style={{ ...badgeStyles, background: "transparent" }} {...props}>
        {children}
      </Badge>
    );
  }

  return (
    <Badge style={badgeStyles} {...props}>
      {children}
    </Badge>
  );
}
